/**
 * Menu mobile V2 — variante B du test A/B sur le menu mobile.
 * Composant autonome (n'étend pas header-drawer/MenuDrawer : l'ouverture
 * est un overlay plein écran, pas un tiroir latéral). Réutilise les
 * utilitaires globaux trapFocus / removeTrapFocus définis dans global.js.
 * Le tracking est délégué à window.MenuTracking (assets/menu-tracking.js), commun aux variantes A et B.
 */
class MobileMenuV2 extends HTMLElement {
  connectedCallback() {
    // Une seule initialisation par élément : jamais d'écouteurs (donc d'événements) en double
    if (this.initialized) return;
    this.trigger = this.querySelector('.mobile-menu-v2__trigger');
    this.panel = this.querySelector('.mobile-menu-v2__panel');
    this.closeButton = this.querySelector('[data-mmv2-close]');
    this.tabs = Array.from(this.querySelectorAll('.mobile-menu-v2__tab'));
    this.searchToggle = this.querySelector('[data-mmv2-search-toggle]');
    this.searchView = this.querySelector('.mobile-menu-v2__search');
    this.searchInput = this.querySelector('[data-mmv2-search-input]');
    this.searchResults = this.querySelector('[data-mmv2-search-results]');
    this.searchBack = this.querySelector('[data-mmv2-search-back]');
    this.searchCache = new Map();

    if (!this.trigger || !this.panel) return;
    this.initialized = true;

    // Le panneau est déplacé en enfant direct de <body> : le header sticky
    // applique un transform à .section-header quand il se masque/réapparaît
    // au scroll, ce qui piège tout descendant en position:fixed dans ce
    // bloc englobant au lieu du viewport (la croix de fermeture pouvait
    // alors se retrouver hors-écran juste après l'ouverture).
    document.body.appendChild(this.panel);

    // Le panneau est rendu avec l'attribut hidden pour ne jamais apparaître
    // pendant le chargement de la page. On le prépare (images comprises)
    // quelques secondes après le load, quand le navigateur est au repos.
    const schedulePrepare = () => {
      setTimeout(() => {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => this.prepare());
        } else {
          this.prepare();
        }
      }, 3000);
    };
    if (document.readyState === 'complete') {
      schedulePrepare();
    } else {
      window.addEventListener('load', schedulePrepare, { once: true });
    }

    this.trigger.addEventListener('click', this.open.bind(this));
    this.closeButton?.addEventListener('click', () => this.close('croix'));

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.selectTab(tab));
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const nextIndex = (index + (event.key === 'ArrowRight' ? 1 : this.tabs.length - 1)) % this.tabs.length;
        const nextTab = this.tabs[nextIndex];
        nextTab.focus();
        this.selectTab(nextTab);
      });
    });

    if (this.searchToggle && this.searchView && this.searchInput && this.searchResults) {
      this.searchToggle.addEventListener('click', () => {
        if (this.isSearchOpen()) {
          this.closeSearch();
        } else {
          this.openSearch();
        }
      });
      this.searchBack?.addEventListener('click', () => this.closeSearch());
      this.searchInput.addEventListener('input', () => this.onSearchInput());
    }

    this.panel.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || this.panel.getAttribute('data-open') !== 'true') return;
      if (this.isSearchOpen()) {
        this.closeSearch();
      } else {
        this.close('echap');
      }
    });

    window.MenuTracking?.bindClicks(this.panel);

    // Ouverture d'une famille de l'accordéon (onglet Produits). L'événement toggle ne remonte pas.
    this.panel.querySelectorAll('details[data-mmv2-family]').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        this.track(`menu_accordion_open:${details.dataset.mmv2Family}`, details.querySelector('summary'), {
          menu_label: details.dataset.mmv2FamilyLabel || '',
          menu_position: parseInt(details.dataset.mmv2FamilyPosition, 10) || 0,
        });
      });
    });

    // Recherche validée avec la touche Entrée (sans passer par un résultat)
    const searchForm = this.searchView?.querySelector('form');
    searchForm?.addEventListener('submit', () => {
      const term = (this.searchInput?.value || '').trim();
      window.MenuTracking?.searchValidated();
      this.track(`menu_search_submit:${term}`, searchForm, { menu_search_term: term });
    });
  }

  selectTab(selected) {
    this.tabs.forEach((tab) => {
      const isSelected = tab === selected;
      tab.setAttribute('aria-selected', String(isSelected));
      tab.tabIndex = isSelected ? 0 : -1;
      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (panel) panel.hidden = !isSelected;
    });
    const panelsContainer = this.querySelector('.mobile-menu-v2__panels');
    if (panelsContainer) panelsContainer.scrollTop = 0;
    this.track('menu_tab_click', selected, {
      menu_section: this.getTabName(selected),
      menu_detail: 'click',
      menu_position: this.tabs.indexOf(selected) + 1,
    });
  }

  getTabName(tab) {
    return tab.textContent.trim().toLowerCase();
  }

  // Onglet affiché à l'ouverture : « default » pour le premier onglet, « restored » si un autre est resté sélectionné
  trackTabView() {
    const selected = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
    if (!selected) return;
    const position = this.tabs.indexOf(selected) + 1;
    this.track('menu_tab_view', selected, {
      menu_section: this.getTabName(selected),
      menu_detail: position === 1 ? 'default' : 'restored',
      menu_position: position,
    });
  }

  prepare() {
    if (!this.panel.hidden) return false;
    this.panel.hidden = false;
    return true;
  }

  open() {
    // Ouverture avant la préparation différée : on force un reflow après
    // avoir retiré hidden pour conserver la transition d'ouverture.
    if (this.prepare()) void this.panel.offsetWidth;
    this.panel.setAttribute('data-open', 'true');
    this.trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('mobile-menu-v2-open');
    if (typeof trapFocus === 'function') trapFocus(this.panel, this.closeButton || this.panel);
    window.MenuTracking?.menuOpened('burger');
    this.trackTabView();
  }

  isSearchOpen() {
    return this.panel.getAttribute('data-search') === 'true';
  }

  openSearch() {
    this.searchView.hidden = false;
    this.panel.setAttribute('data-search', 'true');
    this.searchToggle.setAttribute('aria-expanded', 'true');
    // focus() doit rester synchrone dans le clic : sinon iOS n'ouvre pas le clavier.
    this.searchInput.focus();
    if (typeof trapFocus === 'function') trapFocus(this.panel, this.searchInput);
    window.MenuTracking?.searchStarted();
    this.track('menu_search_open:loupe');
  }

  closeSearch({ restoreFocus = true, reason = 'fermeture_recherche' } = {}) {
    if (!this.searchView || !this.isSearchOpen()) return;
    window.MenuTracking?.searchEnded(reason);
    clearTimeout(this.searchTimer);
    this.searchController?.abort();
    this.searchTerm = '';
    this.searchInput.value = '';
    this.searchResults.innerHTML = '';
    this.searchResults.classList.remove('is-loading');
    this.searchView.hidden = true;
    this.panel.removeAttribute('data-search');
    this.searchToggle.setAttribute('aria-expanded', 'false');
    if (restoreFocus && typeof trapFocus === 'function') trapFocus(this.panel, this.searchToggle);
  }

  onSearchInput() {
    const term = this.searchInput.value.trim();
    window.MenuTracking?.searchTyped(term);
    clearTimeout(this.searchTimer);

    if (term.length < 2) {
      this.searchController?.abort();
      this.searchTerm = '';
      this.searchResults.innerHTML = '';
      this.searchResults.classList.remove('is-loading');
      return;
    }

    this.searchTimer = setTimeout(() => this.runSearch(term), 300);
  }

  async runSearch(term) {
    this.searchTerm = term;
    const cacheKey = term.toLowerCase();

    if (this.searchCache.has(cacheKey)) {
      this.renderSearchResults(this.searchCache.get(cacheKey));
      return;
    }

    // Une seule requête à la fois : une réponse lente d'une frappe précédente
    // ne doit jamais écraser les résultats de la recherche en cours.
    this.searchController?.abort();
    this.searchController = new AbortController();
    this.searchResults.classList.add('is-loading');

    const baseUrl = window.routes?.predictive_search_url || '/search/suggest';
    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'product,collection',
      'resources[limit]': '10',
      'resources[limit_scope]': 'each',
      section_id: 'mobile-menu-v2-search',
    });

    try {
      const response = await fetch(`${baseUrl}?${params.toString()}`, { signal: this.searchController.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const text = await response.text();
      const results = new DOMParser().parseFromString(text, 'text/html').querySelector('.mmv2-search-results');
      const html = results ? results.outerHTML : '';

      this.searchCache.set(cacheKey, html);
      if (term !== this.searchTerm) return;
      this.renderSearchResults(html);
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (term !== this.searchTerm) return;
      this.searchResults.classList.remove('is-loading');
      this.searchResults.innerHTML =
        '<p class="mmv2-search-results__error">Recherche indisponible pour le moment, réessaie dans un instant.</p>';
    }
  }

  renderSearchResults(html) {
    this.searchResults.classList.remove('is-loading');
    this.searchResults.innerHTML = html;
    this.searchResults.scrollTop = 0;
  }

  close(reason = 'croix') {
    this.closeSearch({ restoreFocus: false, reason: 'fermeture_menu' });
    this.panel.setAttribute('data-open', 'false');
    this.trigger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('mobile-menu-v2-open');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.trigger);
    window.MenuTracking?.menuClosed(reason);
  }

  track(eventString, element, extra) {
    window.MenuTracking?.track(eventString, element, extra);
  }
}

customElements.define('mobile-menu-v2', MobileMenuV2);
